/**
 * A declared system (YAG-1108, spec 2026-10-30 section 3): a named YAML saying
 * which repositories run together, the compose file that starts their shared
 * services, the managed databases, defaults and the workspace variables it
 * needs. A Team picks one, and a build of any member starts inside it.
 *
 * "System" is the customer's word; the code says declared system, because
 * `SystemEnvironment` and `system_environments` already name the
 * Machines-explored System.
 *
 * This module runs in the browser too, so it uses nothing from Node.
 */
import { z } from "zod";
import { composeFilePathSchema, composeServiceName, environmentFromSchema, qaPortSchema, qaServiceNameSchema, qaVariables, variableName, } from "./qaEnvironmentSchema.js";
import { EnvironmentDocumentError } from "./environmentDocument.js";
export const DECLARED_SYSTEM_MAX_REPOS = 20;
/** A system's name: a lowercase slug, unique in its workspace. */
export const declaredSystemNameSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "Name the system with lowercase letters, digits and dashes (at most 40)");
/** A value under this name is a credential, never a default in the document. */
const SECRET_LOOKING_NAME = /KEY|TOKEN|SECRET|PASSWORD/i;
const repositoryKey = z
    .string()
    .trim()
    .max(200)
    .regex(/^[^/\s]+\/[^/\s]+$/, "Name a repository as owner/name");
const database = z.object({ port: qaPortSchema }).strict();
const databasesSchema = z
    .object({
    postgres: database.optional(),
    redis: database.optional(),
    mysql: database.optional(),
})
    .strict();
const registryHost = z
    .string()
    .trim()
    .min(1)
    .max(253)
    .regex(/^[A-Za-z0-9][A-Za-z0-9.-]*(:\d{1,5})?$/, "Name the registry by its host");
/** The managed kinds a compose file can start its own copy of. */
export const declaredSystemManagedKindSchema = z.enum(["postgres", "redis", "mysql"]);
/**
 * The registry the compose file pulls from: a host alone (no sign-in), or a
 * host with the workspace variables, by NAME, a build signs in with.
 */
const registryDocument = z.union([
    registryHost,
    z.object({ host: registryHost, user: variableName.optional(), token: variableName }).strict(),
]);
/** The stored registry; a row saved when it was a bare host reads as that host. */
const registryConfig = z.union([
    registryHost.transform((host) => ({ host })),
    z
        .object({
        host: registryHost,
        userVariable: variableName.optional(),
        tokenVariable: variableName.optional(),
    })
        .strict(),
]);
const memberSchema = z
    .object({
    /** Checked out at its base branch for context and services, never the ticket's target unless the ticket targets it. */
    shared: z.boolean().optional(),
    service: qaServiceNameSchema.optional(),
    port: qaPortSchema.optional(),
    env: qaVariables.optional(),
    /** Compose services this member needs started, a subset of `compose.services`. */
    services: z.array(composeServiceName).max(20).optional(),
})
    .strict();
export const systemDocumentSchema = z
    .object({
    version: z.literal(1).optional(),
    repos: z
        .record(repositoryKey, memberSchema.nullable())
        .refine((repos) => Object.keys(repos).length >= 1, "List at least one repository")
        .refine((repos) => Object.keys(repos).length <= DECLARED_SYSTEM_MAX_REPOS, `List at most ${DECLARED_SYSTEM_MAX_REPOS} repositories`),
    compose: z
        .object({
        repository: repositoryKey,
        file: composeFilePathSchema.optional(),
        services: z.array(composeServiceName).max(20).optional(),
    })
        .strict()
        .optional(),
    databases: databasesSchema.optional(),
    env: z.object({ defaults: qaVariables.optional() }).strict().optional(),
    needs: z.array(variableName).max(50).optional(),
    registry: registryDocument.optional(),
})
    .strict();
const repoId = z.uuid().transform((value) => value.toLowerCase());
/** The parsed, stored configuration (`declared_systems.config`). */
export const declaredSystemConfigSchema = z
    .object({
    version: z.literal(1),
    repos: z
        .array(z
        .object({
        repoId,
        fullName: z.string().min(1).max(200),
        shared: z.boolean(),
        service: qaServiceNameSchema.optional(),
        port: qaPortSchema.optional(),
        env: qaVariables,
        services: z.array(composeServiceName).max(20).optional(),
    })
        .strict())
        .min(1)
        .max(DECLARED_SYSTEM_MAX_REPOS),
    compose: z
        .object({
        repoId,
        composeFile: composeFilePathSchema,
        services: z.array(composeServiceName).max(20),
        environmentFrom: environmentFromSchema,
        /**
         * The managed kinds the compose file starts its own copy of, read at
         * save; absent on a row saved before it was kept.
         */
        managed: z.array(declaredSystemManagedKindSchema).max(3).optional(),
        /**
         * The compose file read for each non-shared member as the primary
         * (hosts and ports rewritten for it), by lowercase repoId; absent on
         * a row saved before it was kept. `environmentFrom` is the first.
         */
        environmentFromByRepo: z
            .record(z.string(), environmentFromSchema)
            .refine((reads) => Object.keys(reads).length <= DECLARED_SYSTEM_MAX_REPOS, `At most ${DECLARED_SYSTEM_MAX_REPOS} compose reads`)
            .optional(),
    })
        .strict()
        .optional(),
    databases: databasesSchema,
    envDefaults: qaVariables,
    needs: z.array(variableName).max(50),
    registry: registryConfig.optional(),
})
    .strict();
function lower(value) {
    return value.trim().toLowerCase();
}
function refuseNewSecrets(values, held, path) {
    for (const [name, value] of Object.entries(values ?? {})) {
        if (!value || !SECRET_LOOKING_NAME.test(name) || held?.[name] === value)
            continue;
        throw new EnvironmentDocumentError([...path, name], `${name} looks like a secret; put it under Connections as a workspace variable`);
    }
}
/**
 * The repository whose service the compose file's hosts and ports are
 * rewritten for: the first non-shared member other than the compose
 * repository, else the first other member, else the compose repository itself.
 */
export function systemPrimaryRepoId(members, composeRepoId) {
    const others = members.filter((member) => lower(member.repoId) !== lower(composeRepoId));
    return (others.find((member) => !member.shared) ?? others[0])?.repoId ?? composeRepoId;
}
/**
 * Every repository the compose file is read for: each non-shared member other
 * than the compose repository, in order, else {@link systemPrimaryRepoId}'s one.
 */
export function systemPrimaryRepoIds(members, composeRepoId) {
    const apps = members
        .filter((member) => !member.shared && lower(member.repoId) !== lower(composeRepoId))
        .map((member) => member.repoId);
    return apps.length ? apps : [systemPrimaryRepoId(members, composeRepoId)];
}
/** A stored configuration as its document; defaults are left out. */
export function documentFromSystemConfig(config) {
    const repos = {};
    for (const member of config.repos)
        repos[member.fullName] = {
            ...(member.shared ? { shared: true } : {}),
            ...(member.service ? { service: member.service } : {}),
            ...(member.port !== undefined ? { port: member.port } : {}),
            ...(Object.keys(member.env).length ? { env: member.env } : {}),
            ...(member.services?.length ? { services: member.services } : {}),
        };
    const doc = { repos };
    if (config.compose) {
        const compose = config.compose;
        const holder = config.repos.find((member) => lower(member.repoId) === lower(compose.repoId));
        doc.compose = {
            repository: holder?.fullName ?? `unknown:${compose.repoId}`,
            file: compose.composeFile,
            ...(compose.services.length ? { services: compose.services } : {}),
        };
    }
    const { postgres, redis, mysql } = config.databases;
    if (postgres || redis || mysql)
        doc.databases = {
            ...(postgres ? { postgres } : {}),
            ...(redis ? { redis } : {}),
            ...(mysql ? { mysql } : {}),
        };
    if (Object.keys(config.envDefaults).length)
        doc.env = { defaults: config.envDefaults };
    if (config.needs.length)
        doc.needs = config.needs;
    if (config.registry) {
        const { host, userVariable, tokenVariable } = config.registry;
        doc.registry = tokenVariable
            ? { host, ...(userVariable ? { user: userVariable } : {}), token: tokenVariable }
            : host;
    }
    return doc;
}
/**
 * A document as the stored configuration. Every refusal is an
 * `EnvironmentDocumentError` at the document path it is about: a repository
 * the workspace has not connected, one listed twice (names compare without
 * case), a compose repository that is not a member, a member's service the
 * compose section does not start, a new secret-looking default.
 */
export function systemConfigFromDocument(doc, ctx) {
    const seen = new Set();
    const members = [];
    const composeServices = doc.compose?.services ?? [];
    for (const [key, entry] of Object.entries(doc.repos)) {
        const member = entry ?? {};
        if (seen.has(lower(key)))
            throw new EnvironmentDocumentError(["repos", key], `${key} is listed twice`);
        seen.add(lower(key));
        const repo = ctx.resolve(key.trim());
        if (!repo)
            throw new EnvironmentDocumentError(["repos", key], `No repository named ${key.trim()} in this workspace`);
        if (members.some((held) => lower(held.repoId) === lower(repo.repoId)))
            throw new EnvironmentDocumentError(["repos", key], `${key} is listed twice`);
        for (const [index, service] of (member.services ?? []).entries())
            if (!composeServices.includes(service))
                throw new EnvironmentDocumentError(["repos", key, "services", index], doc.compose
                    ? `${service} is not one of compose.services`
                    : `${service} needs a compose section that starts it`);
        const held = ctx.current?.repos.find((current) => lower(current.repoId) === lower(repo.repoId));
        refuseNewSecrets(member.env, held?.env, ["repos", key, "env"]);
        members.push({
            repoId: repo.repoId.toLowerCase(),
            fullName: repo.fullName ?? key.trim(),
            shared: member.shared ?? false,
            ...(member.service ? { service: member.service } : {}),
            ...(member.port !== undefined ? { port: member.port } : {}),
            env: member.env ?? {},
            ...(member.services?.length ? { services: member.services } : {}),
        });
    }
    refuseNewSecrets(doc.env?.defaults, ctx.current?.envDefaults, ["env", "defaults"]);
    let compose;
    if (doc.compose) {
        const section = doc.compose;
        const holderKey = Object.keys(doc.repos).find((key) => lower(key) === lower(section.repository));
        const holder = holderKey
            ? members[Object.keys(doc.repos).indexOf(holderKey)]
            : members.find((member) => lower(member.fullName) === lower(section.repository));
        if (!holder)
            throw new EnvironmentDocumentError(["compose", "repository"], `${section.repository} is not one of this system's repos; list it under repos`);
        if (!ctx.environmentFrom)
            throw new EnvironmentDocumentError(["compose", "repository"], "The compose file can only be read when the system is saved");
        const request = {
            repoId: holder.repoId,
            ...(section.file ? { file: section.file } : {}),
            services: composeServices,
        };
        // One read per non-shared member, so each builds with hosts and ports
        // rewritten for itself; the managed kinds are the first read's.
        const environmentFromByRepo = {};
        let managed = [];
        for (const [index, primaryRepoId] of systemPrimaryRepoIds(members, holder.repoId).entries()) {
            const { managed: kinds, ...read } = ctx.environmentFrom(request, primaryRepoId);
            environmentFromByRepo[lower(primaryRepoId)] = read;
            if (index === 0)
                managed = [...new Set(kinds ?? [])];
        }
        const environmentFrom = Object.values(environmentFromByRepo)[0];
        compose = {
            repoId: holder.repoId,
            composeFile: environmentFrom.composeFile,
            services: composeServices,
            environmentFrom,
            managed,
            environmentFromByRepo,
        };
    }
    return declaredSystemConfigSchema.parse({
        version: 1,
        repos: members,
        ...(compose ? { compose } : {}),
        databases: doc.databases ?? {},
        envDefaults: doc.env?.defaults ?? {},
        needs: doc.needs ?? [],
        ...(doc.registry
            ? {
                registry: typeof doc.registry === "string"
                    ? { host: doc.registry }
                    : {
                        host: doc.registry.host,
                        ...(doc.registry.user ? { userVariable: doc.registry.user } : {}),
                        tokenVariable: doc.registry.token,
                    },
            }
            : {}),
    });
}
//# sourceMappingURL=systemDocument.js.map